/**
 * voice-v8 — Karte „Postausgang schreiben“ (28.10.2026, Autonomie-Modus B10 Teil b; Server: server/konfig-ablage.ts).
 *
 * Zeigt, welche FESTE Variable woher kommt: SES_SMTP_USER und SES_SMTP_PASS aus zwei Namen der Ablage, HOST und PORT fest. Nie ein Wert.
 * „Schreiben“ schickt den festen Wortlaut aus Kennung und Kurzwert des Servers (shared/konfig-ablage.ts), „Nicht schreiben“ den anderen.
 * Nur ein ECHTER Tipp sendet (knopf-paar.ts, `isTrusted`); der Server löst nur ein, wenn die Nachricht vom Knopf der Karte kommt.
 * Alles als Text (textContent). Aussehen: dieselbe ruhige Liste wie die Zugangswerte-Karte (frontend/style.css).
 */
import { KONFIG_KARTE, KONFIG_KENNUNG, KONFIG_KURZWERT, KONFIG_PORT, konfigKnopfJa, konfigKnopfNein, SES_HOST_FORM } from "../../../shared/konfig-ablage.js";
import { absatz, baueKarte, kartenTitel } from "../karte.js";
import { unwrapResult } from "./hilfe.js";
import { fristAus, haengeKnopfReiheAn } from "./knopf-paar.js";

const TITEL = "Postausgang schreiben";
const ABGELAUFEN_SATZ = "Diese Karte gilt nicht mehr (15 Minuten). Bitte Vespera, sie neu vorzulegen.";
const NAME_FORM = /^[a-z0-9][a-z0-9._-]{1,59}$/;

interface Felder {
  id: string;
  kurz: string;
  benutzer: string;
  passwort: string;
  host: string;
}

function felderAus(result: unknown): Felder | null {
  const r = unwrapResult(result);
  const { vorschlag_id: id, kurzwert: kurz, benutzer, passwort, host, port } = r;
  if (r.wartet_auf_bestaetigung !== true) return null;
  if (typeof id !== "string" || !KONFIG_KENNUNG.test(id) || typeof kurz !== "string" || !KONFIG_KURZWERT.test(kurz)) return null;
  if (typeof benutzer !== "string" || !NAME_FORM.test(benutzer) || typeof passwort !== "string" || !NAME_FORM.test(passwort)) return null;
  if (typeof host !== "string" || !SES_HOST_FORM.test(host) || port !== KONFIG_PORT) return null;
  return { id, kurz, benutzer, passwort, host };
}

/** Ist dieses Ergebnis die Konfig-Karte (vom Server gefüllt, alle Felder in fester Form)? */
export function istKonfigRueckfrage(tool: string, result: unknown): boolean {
  return tool === KONFIG_KARTE && felderAus(result) !== null;
}

/** Die Karte — `null` ohne gültige Felder. */
export function buildKonfigCard(result: unknown): HTMLElement | null {
  const f = felderAus(result);
  if (!f) return null;
  const el = baueKarte("konfig-ablage");
  el.appendChild(kartenTitel(TITEL));
  el.appendChild(absatz("Vespera schreibt den abgelegten SES-Zugang in die Konfiguration. Die Werte selbst sieht sie nicht."));
  const liste = document.createElement("ul");
  liste.className = "zugangswerte-liste";
  const zeilen: ReadonlyArray<[string, string]> = [
    ["SES_SMTP_USER", `aus der Ablage: ${f.benutzer}`],
    ["SES_SMTP_PASS", `aus der Ablage: ${f.passwort}`],
    ["SES_SMTP_HOST", f.host],
    ["SES_SMTP_PORT", `${KONFIG_PORT}, nur verschlüsselt`],
  ];
  for (const [variable, quelle] of zeilen) {
    const li = document.createElement("li");
    const name = document.createElement("span");
    name.className = "zugangswerte-name";
    name.textContent = variable;
    const herkunft = document.createElement("span");
    herkunft.className = "zugangswerte-herkunft";
    herkunft.textContent = quelle;
    li.append(name, herkunft);
    liste.appendChild(li);
  }
  el.appendChild(liste);
  const regel = absatz(
    `Zuerst geht eine Testmail an dich. Nur wenn sie durchgeht, wird geschrieben. Gilt erst nach dem nächsten Neustart. Prüfwert ${f.kurz}.`,
  );
  regel.classList.add("zugangswerte-regel");
  el.appendChild(regel);
  haengeKnopfReiheAn(el, {
    knoepfe: [
      {
        text: "Schreiben",
        primaer: true,
        ja: true,
        nachricht: konfigKnopfJa(f.id, f.kurz),
        name: `Postausgang schreiben: ${f.host}`,
        gesendet: "Gesendet — Testmail läuft, dann wird geschrieben.",
      },
      { text: "Nicht schreiben", ja: false, nachricht: konfigKnopfNein(f.id), name: "Postausgang nicht schreiben", gesendet: "Gesendet — nichts wird geschrieben." },
    ],
    bisMs: fristAus((unwrapResult(result) as { gueltig_bis_ms?: unknown }).gueltig_bis_ms),
    abgelaufenSatz: ABGELAUFEN_SATZ,
  });
  return el;
}
